import { Group, Paper, SegmentedControl, Text, Button } from '@/components/ui';
// Range mode comes from @mantine/dates directly: the @/components/ui wrapper is typed
// for a single date. Every other range picker in the codebase does the same.
import { DatePickerInput } from '@mantine/dates';
import { Select, MultiSelect } from '@/components/ui/inputs';
import { IconRefresh } from '@tabler/icons-react';
import dayjs from 'dayjs';
import {
  DATE_PRESETS, GRANULARITIES, PLATFORMS, type Granularity, type Platform,
} from '../utils/socialMediaConstants';
import { periodLabel, resolvePreset, type AnalyticsFilter } from '../utils/analyticsHelpers';

interface Props {
  filter: AnalyticsFilter;
  onChange: (next: AnalyticsFilter) => void;
  onRefresh?: () => void;
  /** Hidden on per-platform pages, where the platform is fixed by the route. */
  showPlatform?: boolean;
  accountOptions?: { value: string; label: string }[];
  loading?: boolean;
}

/**
 * The controls that change constantly: date range, granularity, platform.
 *
 * Sticky, because a dozen charts scroll underneath it and the range is the thing you
 * keep reaching for. Anything longer-tail belongs in a filter drawer instead.
 */
export function AnalyticsToolbar({
  filter, onChange, onRefresh, showPlatform = true, accountOptions = [], loading,
}: Props) {
  // pageFiltersStore persists via JSON.stringify, so dates live as ISO strings and are
  // only converted at this boundary.
  const rangeValue: [Date | null, Date | null] = [
    filter.from ? dayjs(filter.from).toDate() : null,
    filter.to ? dayjs(filter.to).toDate() : null,
  ];

  const setPreset = (preset: string) => {
    const p = preset as AnalyticsFilter['preset'];
    onChange({ ...filter, preset: p, ...resolvePreset(p, filter) });
  };

  const setRange = (value: [Date | null, Date | null]) => {
    const [from, to] = value;
    if (!from || !to) return;
    // Choosing dates by hand implies a custom range, so the preset must stop overriding it.
    onChange({
      ...filter,
      preset: 'custom',
      from: dayjs(from).format('YYYY-MM-DD'),
      to: dayjs(to).format('YYYY-MM-DD'),
    });
  };

  return (
    <Paper withBorder p="sm" radius="md" className="no-print"
           pos="sticky" top={0} style={{ zIndex: 3 }}>
      <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm">
        <Group align="flex-end" gap="sm" wrap="wrap">
          <Select
            label="Period"
            data={DATE_PRESETS.map((p) => ({ value: p.value, label: p.label }))}
            value={filter.preset}
            onChange={(v) => v && setPreset(v)}
            w={150}
            allowDeselect={false}
          />

          <DatePickerInput
            type="range"
            label="Date range"
            value={rangeValue}
            onChange={(value) => setRange(value as [Date | null, Date | null])}
            maxDate={new Date()}
            w={240}
          />

          <div>
            <Text size="sm" fw={500} mb={4}>Granularity</Text>
            <SegmentedControl
              size="sm"
              value={filter.granularity}
              onChange={(v) => onChange({ ...filter, granularity: v as Granularity })}
              data={GRANULARITIES.map((g) => ({ value: g.value, label: g.label }))}
            />
          </div>

          {showPlatform && (
            <MultiSelect
              label="Platforms"
              placeholder="All platforms"
              data={PLATFORMS.map((p) => ({ value: p, label: p }))}
              value={filter.platforms}
              onChange={(v) => onChange({ ...filter, platforms: v as Platform[] })}
              clearable
              w={220}
            />
          )}

          {accountOptions.length > 0 && (
            <MultiSelect
              label="Accounts"
              placeholder="All accounts"
              data={accountOptions}
              value={filter.accountIds}
              onChange={(v) => onChange({ ...filter, accountIds: v })}
              clearable
              w={220}
            />
          )}
        </Group>

        <Group gap="xs" align="center">
          <Text size="xs" c="dimmed">{periodLabel(filter)}</Text>
          {onRefresh && (
            <Button
              variant="light"
              size="sm"
              leftSection={<IconRefresh size={14} />}
              onClick={onRefresh}
              loading={loading}
            >
              Refresh
            </Button>
          )}
        </Group>
      </Group>
    </Paper>
  );
}
